import { Component, effect, EventEmitter, forwardRef, inject, Injector, Input, OnInit, Output, Provider } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { NotificationOptionsFacade } from 'mbs-ui-shared@facades/notificationOptions.facade';
import { NotificationRuleSettings, NotificationSettings } from 'mbs-ui-shared@models';
import { NotificationPerPlanSettings } from 'mbs-ui-shared@models/Notification';
import { recipientValidator } from 'mbs-ui-shared@utils/recipient-validator';
import { BehaviorSubject, of } from 'rxjs';
import { filter, map, skip, switchMap, take } from 'rxjs/operators';
import { NotificationsStepValue } from 'wizards/models/notifications-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';

import {
  ButtonComponent,
  CardComponent,
  FormGroupComponent,
  GroupButtonComponent,
  RadioComponent,
  SwitcherComponent,
  TextEllipsisComponent
} from 'mbs-ui-kit';

import { NotificationsCheckboxesComponent } from 'mbs-ui-shared@components/notifications-checkboxes/notifications-checkboxes.component';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

import { RouterLink } from '@angular/router';
import { StepBase } from 'wizards/steps/StepBase.class';

const NotificationsStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => NotificationsStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-notifications-step',
  templateUrl: './notifications-step.component.html',
  styleUrls: ['./notifications-step.component.scss'],
  providers: [NotificationsStepValueAccessor],
  imports: [
    ButtonComponent,
    CardComponent,
    CommonModule,
    FormGroupComponent,
    GroupButtonComponent,
    NgbTooltipModule,
    NotificationsCheckboxesComponent,
    RadioComponent,
    ReactiveFormsModule,
    RouterLink,
    SwitcherComponent,
    TextEllipsisComponent,
    I18NextPipe
  ]
})
export class NotificationsStepComponent extends StepBase<NotificationsStepValue> implements OnInit {
  private fb = inject(FormBuilder);
  ability = inject(AbilityService);
  mainService: RemoteManagementWizardsService;
  private notificationFacade = inject(NotificationOptionsFacade);
  private computersFacade = inject(ComputersFacade);
  private companiesFacade = inject(CompaniesFacade);
  private injector = inject(Injector);
  i18nPipe = inject(I18NextPipe);

  @Input()
  set notification(value: NotificationPerPlanSettings) {
    this.#notification = value;
    this.notificationForm && this.notificationForm.patchValue(this.#notification, { emitEvent: false });
  }

  @Output() notificationChange = new EventEmitter<NotificationPerPlanSettings>();

  #notification: NotificationPerPlanSettings;

  public readonly RoutingPath = RoutingPath;
  public readonly elementsSelector = {
    name: {
      mainNotificationSwitch: 'mainNotificationSwitch',
      mainInfoBlock: 'mainInfoBlock'
    }
  };

  public notificationForm: FormGroup<{
    enabled: FormControl<boolean>;
    failure: FormControl<NotificationRuleSettings>;
    warning: FormControl<NotificationRuleSettings>;
    success: FormControl<NotificationRuleSettings>;
    noSuccess: FormControl<NotificationRuleSettings>;
  }>;
  public globalNotificationForm: FormGroup<{
    failure: FormControl<NotificationRuleSettings>;
    warning: FormControl<NotificationRuleSettings>;
    success: FormControl<NotificationRuleSettings>;
    noSuccess: FormControl<NotificationRuleSettings>;
  }>;

  public isProvider: boolean;
  public companyId$ = new BehaviorSubject<string>(null);
  public globalNotificationSettings$ = new BehaviorSubject<NotificationSettings>(null);
  public userName = '';

  public set notificationFormEnabled(value: boolean) {
    this.notificationForm.get('enabled').patchValue(value);
  }
  public get notificationFormEnabled(): boolean {
    return this.notificationForm?.get('enabled').value || false;
  }

  public get prefixType(): string {
    return this.isBackupNotificationsMode ? 'backup' : 'restore';
  }

  public get isBackupNotificationsMode(): boolean {
    return this.isBackupPlan || this.isIBBPlan || this.isHyperV || this.isVMWare || this.isSQLPlan || this.isProxmox;
  }

  constructor() {
    const auth = inject(AuthFacade);
    const mainService = inject(RemoteManagementWizardsService);

    super();
    this.mainService = mainService;

    auth.currentUser$.pipe(take(1), untilDestroyed(this)).subscribe((user) => {
      this.isProvider = user.IsProvider;
    });

    effect(() => {
      const settings = this.notificationFacade.settings();

      settings && this.globalNotificationSettings$.next(settings);
    });
  }

  ngOnInit(): void {
    this.initForm();
    this.initStreams();
  }

  initForm(): void {
    if (!this.isRDMode) {
      this.notificationForm = this.fb.group({
        enabled: [false],
        failure: [new NotificationRuleSettings(), recipientValidator.bind(this)],
        warning: [new NotificationRuleSettings(), recipientValidator.bind(this)],
        success: [new NotificationRuleSettings(), recipientValidator.bind(this)],
        noSuccess: [new NotificationRuleSettings(), recipientValidator.bind(this)]
      });

      this.globalNotificationForm = this.fb.group({
        failure: [new NotificationRuleSettings()],
        warning: [new NotificationRuleSettings()],
        success: [new NotificationRuleSettings()],
        noSuccess: [new NotificationRuleSettings()]
      });
    }

    this.stepForm = new UntypedFormGroup({
      receiveNotificationOnCompleteGroup: new UntypedFormGroup({
        flag: new FormControl(false),
        case: new FormControl('whenFails')
      })
    });

    this.initFormEvents();
  }

  private initStreams(): void {
    if (this.mainService?.hid) {
      if (!this.isRDMode) {
        this.companyId$
          .pipe(
            filter(Boolean),
            take(1),
            switchMap((companyId) => {
              this.companiesFacade.loadById({ id: companyId, force: true });
              return toObservable(this.companiesFacade.loadedEntityById(companyId), { injector: this.injector }).pipe(
                skip(1),
                filter(Boolean),
                take(1)
              );
            }),
            map((company) => company.notificationSettings),
            switchMap((notificationSettings) => {
              if (
                this.isBackupNotificationsMode
                  ? notificationSettings.customBackupNotification
                  : notificationSettings.customRestoreNotification
              )
                return of(
                  this.isBackupNotificationsMode
                    ? notificationSettings.backupNotificationSettings
                    : notificationSettings.restoreNotificationSettings
                );

              return this.globalNotificationSettings$.pipe(
                filter(Boolean),
                take(1),
                map((settings) =>
                  this.isBackupNotificationsMode ? settings.backupNotificationSettings : settings.restoreNotificationSettings
                )
              );
            }),
            untilDestroyed(this)
          )
          .subscribe((data) => {
            this.globalNotificationForm.patchValue(data);
            this.globalNotificationForm.disable();
            this.globalNotificationForm.get('success').disable();
            this.globalNotificationForm.get('warning').disable();
            this.globalNotificationForm.get('failure').disable();
          });
      }

      this.computersFacade
        .getByHid$(this.mainService.hid)
        .pipe(take(1), untilDestroyed(this))
        .subscribe((computer) => {
          computer?.company?.id && this.companyId$.next(computer?.company?.id);
          this.userName = computer?.userAccount?.accountName || '';
        });
    }

    if (this.isRDMode) return;

    this.notificationForm.valueChanges.pipe(untilDestroyed(this)).subscribe((settings) => {
      this.#notification = { ...this.#notification, ...settings };
      this.notificationChange.emit(this.#notification);
    });
  }

  updateForm(value: NotificationsStepValue): void {
    this.stepForm.reset(value);

    if (value.receiveNotificationOnCompleteGroup) this.flagChangeHandler(value.receiveNotificationOnCompleteGroup.flag);
  }

  flagChangeHandler(event): void {
    const control = this.stepForm.get('receiveNotificationOnCompleteGroup');
    event ? control.get('case').enable() : control.get('case').disable();
  }
}
